import * as themeCart from '../../scripts/utils/themeCart';
import { getCartSuccess, addToCartFail, setCartActive, setMinicart, setCartUpdating } from '../store/actions/cart';
import { Cart } from '../util/typings'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import { RootState } from '../store/reducers'
import { dispatchToast } from '../store/actions/toast';
import { LineItem } from '../util/typings'
import axios from 'axios';
import assignRemainingAvailablities from '../util/assignRemainingAvailablities';
import getCartVariantsAtLimit from '../util/getCartVariantsAtLimit';
import getCurrencyRoute from '../../scripts/utils/getCurrencyRoute';
import Cookies from 'js-cookie/src/js.cookie';

export interface ICartItem {
  id: number;
  quantity: number;
  properties?: any
  // eslint-disable-next-line camelcase
  sellingPlanId?: number
}

interface ICartUpdateByKey {
  quantity: number;
  paraperties?: any
}


/**
 * Cart Fetch custom hook
 */

export const useCart = () => {

  const dispatch = useDispatch()

  // selectors
  const active: boolean = useSelector((state: RootState) => state.cart.cartActive)
  const minicartOpen: boolean = useSelector((state: RootState) => state.cart.minicartOpen)
  const cart: Cart = useSelector((state: RootState) => state.cart.current)
  const updating: boolean = useSelector((state: RootState) => state.cart.updating)
  const items = (cart.items as LineItem[])

  const removeVariantUnderlayer = () => {
    const productCards = document.querySelectorAll('[data-product-card]');
    if (!productCards) return;
    productCards.forEach(productCard => {
      const productVariantHover = productCard?.querySelector('[data-open-product-variants]');
      productVariantHover?.classList.remove('open');
    })
  }

  //open minicart
  const openMinicart = () => {
    dispatch(setMinicart(true))
  }
  const closeMinicart = () => {
    dispatch(setMinicart(false));
    removeVariantUnderlayer();
  }
  const toggleMinicart = () => {
    const open: Cart = useSelector((state: RootState) => state.cart.minicartOpen)
    dispatch(setMinicart(!open))
  }


  useEffect(() => {
    // If we're on the b2b site we want the toast instead of the minicart,
    // this ensures the class doesn't get added to the body
    !window['theme'].isb2b ? document.body.classList.toggle('minicart-open', minicartOpen) : null
  }, [minicartOpen])


  useEffect(() => {
    if (items.length < 1){
      closeMinicart()
    }
  }, [items])

  const cartHasTriggerSku = (skus = [], items) => {
    if (!skus || !items) return false;

    for (const item of items) {
      if (skus.includes(item.sku)) {
        return true
      }
    }

    return false;
  }

  //does the id exist in the set of items
  const cartHasId = (id, items) => {
    if (!id || !items) return false;

    for (const item of items) {
      if (item.id === id) {
        return item.key
      }
    }

    return false;
  }

  const applyFreeGift = async (cartState) => {
    let change = false;
    for (const freeGifts of window['freeCartGifts']) {
      let hasTriggerSku = false
      let freeGiftSkuKey = null
      let hasDiscountApplied = false
      let freeGiftDiscountKey = null
      let hasReachedMinSpend = false

      switch(freeGifts.type) {
        case 'by_sku':
          hasTriggerSku = cartHasTriggerSku(freeGifts.trigger, cartState.items)
          freeGiftSkuKey  = cartHasId(freeGifts.id, cartState.items)

          if (hasTriggerSku && !freeGiftSkuKey) {
            // add the free gift
            // eslint-disable-next-line no-use-before-define
            await addToCart([{
              id: freeGifts.id,
              quantity: 1,
              properties: {'_free_gift': true}
            }])
            change = true;
          }

          if (!hasTriggerSku && freeGiftSkuKey) {
            // remove the free gift
            // eslint-disable-next-line no-use-before-define
            await removeByKey(freeGiftSkuKey)
            change = true;
            if (window.location.pathname.includes('/cart')) {
              location.reload()
            }
          }
          break;
        case 'by_discount_code':
          // If applied code at checkout and come back to cart
          // set the cookie and remove the param set during checkout
          if (localStorage.getItem('bb:checkoutDiscountCode')) {
            Cookies.set('discount_code', localStorage.getItem('bb:checkoutDiscountCode'));
            localStorage.removeItem('bb:checkoutDiscountCode')
          }
          hasDiscountApplied = Cookies.get('discount_code') === freeGifts.trigger
          freeGiftDiscountKey  = cartHasId(freeGifts.id, cartState.items)
          hasReachedMinSpend = cartState.total_price >= freeGifts.order_min

          if (hasDiscountApplied && hasReachedMinSpend && !freeGiftDiscountKey) {
            // add the free gift
            console.log('free gift added to minicart')
            //reload page after funcion is executed to trigger free gift addition to cart
            // eslint-disable-next-line no-use-before-define
            await addToCart(
              [{
                id: freeGifts.id,
                quantity: 1,
                properties: {'_free_gift': true}
              }],
              false,
              () => window.location.pathname.includes('/cart') ? location.reload() : null )
            change = true;
          }

          if ((!hasDiscountApplied || !hasReachedMinSpend) && freeGiftDiscountKey) {
            // remove the free gift
            // eslint-disable-next-line no-use-before-define
            await removeByKey(freeGiftDiscountKey)
            change = true;
            if (window.location.pathname.includes('/cart')) {
              location.reload()
            }
          }
          break;
      }
    }
    return change;
  }

  // get cart
  const getCartState = async (): Promise<Cart|void>   => {
    try {
      const [shopifyCartState, variantsAtLimit] = await Promise.all([
        themeCart.getState(),
        getCartVariantsAtLimit()
      ]);
      const cartState = assignRemainingAvailablities(shopifyCartState, variantsAtLimit)
      if (cartState) {
        const freeGiftChange = await applyFreeGift(cartState)
        if (freeGiftChange) {
          return await getCartState()
        }
        dispatch(getCartSuccess(cartState))
        if (!active) {
          dispatch(setCartActive(true))
        }
        return cartState;
      }
      dispatch(setCartActive(false))
    } catch (e) {
      console.error(e);
      dispatch(setCartActive(false))
    }
  }

  /**
   * Adds an Item / Items to cart
   *
   * @param items the items to add to the cart
   * @param notify whether to display the minicart or not
   * @param callback completion handler
   */
  const addToCart = async (items: ICartItem[], notify = true, callback?: (Cart) => any) => {
    const itemsAddedArray = [];
    dispatch(setCartUpdating(true))
    for (const item of items) {
      const { id, quantity, properties, sellingPlanId } = item;
      try {
        const itemAdded = await axios.post(getCurrencyRoute('cart/add.js'), {
          id: id,
          quantity: quantity,
          properties: properties,
          // eslint-disable-next-line camelcase
          selling_plan: sellingPlanId ? sellingPlanId : null
        }).then((res) => res.data);
        // console.log('item added: ' + itemAdded);

        if (itemAdded && itemAdded.id) {
          itemsAddedArray.push(itemAdded.id)
          //Could dispatch an event here, if used for tracking...
          window.dispatchEvent(new CustomEvent('blubolt:add-to-cart', { detail: { value: itemAdded.price, quantity: itemAdded.quantity } }))
        }
      } catch (error) {
        //you probably want to disable adding more than available at the liquid/component level, but for other cases:
        dispatch(addToCartFail(error));
        dispatch(dispatchToast('error', { customMessage: 'There was a problem adding the item to the cart' }));
      }
    }
    const newCart = await getCartState();

    if (notify) {
      // If we're on the b2b site we want the toast instead of the minicart
      window['theme'].isb2b ? dispatch(dispatchToast('cart', itemsAddedArray)) : openMinicart()
    }
    if (callback) {
      callback(newCart);
    }



    dispatch(setCartUpdating(false))

    return newCart;
  }

  // Update one item
  const updateCartByKey = async (key: string, opts: ICartUpdateByKey) => {

    if (!key || !opts) { return console.error('A key or options are required to update the cart') }

    dispatch(setCartUpdating(true))
    let possibleShortStock = false
    try {
      await themeCart.updateItem(key, opts)
      const newCart = await getCartState()
      if (!newCart) {
        throw new Error ('getCart failing')
      }
      const updatedItem = (newCart as Cart).items.find(item => item.key === key)

      if (opts.quantity) {
        // best effort stock availability response
        if (updatedItem.quantity < opts.quantity) {
          possibleShortStock = true
          dispatch(dispatchToast('error', { customMessage: `Cannot add more than ${updatedItem.quantity} of ${updatedItem.title}` }));
        }
      }
      dispatch(setCartUpdating(false))
      return { newCart, possibleShortStock }

    } catch (error) {
      console.error('Error updating cart:', error.message)
      dispatch(setCartUpdating(false))
    }

  }

  // Remove one item
  const removeByKey = async (key) => {
    dispatch(setCartUpdating(true))
    if (!key) { return console.error('A key is required to remove item from the cart') }

    try {

      await themeCart.removeItem(key)
      const newCart = await getCartState()
      dispatch(setCartUpdating(false))
      return newCart

    } catch (error) {
      console.error('Error removing item from cart:', error.message)
      dispatch(setCartUpdating(false))
    }
  }

  // clear the items out of the cart
  const clearCart = async () => {
    try {
      await themeCart.clearItems()
      return true
    } catch (error) {
      console.error('Error clearing cart items cart:', error.message)
      return false
    }
  }

  // returns the current cart as reflected in redux
  const safelyReturnCurrentCart = () => {
    return getCartState()
      .then(data => data)
      .catch(() => console.error('cart retrieve error'))
  }

  const setDOMHelpers = () => {
    window['blubolt'].cart = {
      currentCart: cart,
      getCurrentCart: safelyReturnCurrentCart,
      syncCart: getCartState,
      addToCart,
      updateItem: updateCartByKey,
      removeItem: removeByKey,
      clear: clearCart,
    };
    window['blubolt'].minicart = {
      clickOpen: (e: Event) => {
        if (!cart.item_count) {
          return true
        }
        if (!window['theme'].isb2b) {
          e.preventDefault()
          return openMinicart()
        }
      },
      open: openMinicart,
      close: closeMinicart,
      toggle: toggleMinicart
    }
    return true
  }

  const initializeCart = async () => {
    await getCartState()
  }

  const useCartReturn = {
    cart,
    items,
    updating,
    minicartOpen,
    openMinicart,
    closeMinicart,
    toggleMinicart,
    initializeCart,
    getCartState,
    setDOMHelpers,
    syncCart: getCartState,
    updateItem: updateCartByKey,
    removeItem: removeByKey,
    addToCart,
    clear: clearCart,
    getCartAttributes: themeCart.getAttributes,
    updateCartAttributes: themeCart.updateAttributes,
    clearCartAttributes: themeCart.clearAttributes,
    getNote: themeCart.getNote,
    updateNote: themeCart.updateNote,
    clearNote: themeCart.clearNote,
    getShippingRates: themeCart.getShippingRates,
  }

  return useCartReturn

}